import api from 'services/api';

export type BookingProps = {
  uuid?: string;
  start: string;
  end: string;
  reason: string;
  mobileUserUuid: string;
  organizationUuid: string;
  resource: string;
  buildingUuid?: string;
  outlookBooking?: boolean;
  outlookOrganizer?: string;
  bookingDate?: any;
  buildingTimezone?: string;
};

const findAllBookings = async () => {
  const response = await api.get<BookingProps[]>('/apiv1/bookings/');
  return response?.data;
};

const findBookingById = async (uuid: string | undefined) => {
  const response = await api.get<BookingProps>(`/apiv1/booking/${uuid}/`);
  return response?.data;
};

const getAllBookingByResourceId = async (uuid: string | undefined) => {
  const response = await api.get<BookingProps>(`/apiv1/bookings/${uuid}/`);
  return response?.data;
};

const createBooking = async (newBooking: BookingProps) => {
  const response = await api.post<BookingProps>('/apiv1/booking/', newBooking);
  return response?.data;
};

const updateBooking = async (uuid: string | undefined, updatedBooking: BookingProps) => {
  const response = await api.put<BookingProps>(`/apiv1/booking/${uuid}/`, updatedBooking);
  return response?.data;
};

const deleteBookingById = async (uuid: string | undefined, resourceUuid: string | undefined) => {
  const response = await api.delete<BookingProps>(`/apiv1/booking/${resourceUuid}/${uuid}/`);
  return response?.data;
};

/**
 * Fetches all the bookings for the given arguments.
 * @param buildingUuid the UUID of the building
 * @param startTimeInBuildingTz the start time (must match building timezone), e.g. 2020-01-23T00:00:00-07:00
 * @param endTimeInBuildingTz the end time (must match building timezone), e.g. 2024-01-23T00:00:00-07:00
 * @returns the list of bookings
 */
const reportBookings = async (
  buildingUuid: string,
  startTimeInBuildingTz: string,
  endTimeInBuildingTz: string
) => {
  const response = await api.get<BookingProps[]>(
    `/apiv1/reports/bookings/${buildingUuid}/${startTimeInBuildingTz}/${endTimeInBuildingTz}/`
  );
  return response?.data;
};

/**
 * This function retrieves live bookings by building UUID based on the building's timezone.
 * @param {string | undefined} building_uuid - The `building_uuid` parameter is a unique identifier for a specific building.
 * @returns The function `findLiveBookingsByBuilding` is returning the data of `BookingProps[]` type from the response of the
 * API call to retrieve live bookings for a specific building UUID based on the building's timezone.
 */
const findLiveBookingsByBuilding = async (building_uuid: string | undefined) => {
  const response = await api.get<BookingProps[]>(`/apiv1/reports/bookings/live/${building_uuid}/`);
  return response?.data;
};

const BookingService = {
  findAllBookings,
  findBookingById,
  getAllBookingByResourceId,
  createBooking,
  updateBooking,
  deleteBookingById,
  reportBookings,
  findLiveBookingsByBuilding
};

export default BookingService;
